import DownloadIcon from '@material-design-icons/svg/filled/download.svg?react';
import type { Release } from '@src/dto';
import { formatDate, formatFileSize } from '../../../utils/stringHelpers';
import {
  CopyableValue,
  Disclosure,
  Field,
  Section,
} from '../../common/detailParts';

interface BuildSectionProps {
  release: Release;
  apkDownloadUrl: string | null;
  arweaveApkUrl: string | null;
}

export function BuildSection({
  release,
  apkDownloadUrl,
  arweaveApkUrl,
}: BuildSectionProps) {
  const downloadUrl = apkDownloadUrl || arweaveApkUrl;
  const showAndroidInfo =
    release.status === 'Processed' || release.status === 'Published';
  const hasFingerprints =
    Boolean(release.releaseFileHash) || Boolean(release.certificateFingerprint);
  const fingerprintCount =
    (release.releaseFileHash ? 1 : 0) +
    (release.certificateFingerprint ? 1 : 0);

  return (
    <Section
      title="Build"
      trailing={
        downloadUrl && (
          <a
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            download
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/5 text-xs font-medium text-white/90 transition-colors"
          >
            <DownloadIcon className="size-3.5 fill-white/80" />
            Download APK
          </a>
        )
      }
      className="mb-4"
    >
      <div className="flex items-baseline justify-between gap-4 pb-5 border-b border-white/5">
        <div className="min-w-0">
          <div
            className="text-sm text-white/90 truncate font-mono"
            title={release.releaseFileName}
          >
            {release.releaseFileName}
          </div>
          <div className="text-xs text-neutral-500 mt-1">
            Uploaded {formatDate(release.created)}
          </div>
        </div>
        <div className="text-sm text-white/90 font-medium tabular-nums flex-shrink-0">
          {formatFileSize(release.releaseFileSize)}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-5 pt-5">
        {showAndroidInfo && release.androidPackage && (
          <Field label="Package" className="col-span-2 md:col-span-1">
            <span className="font-mono text-sm text-white/90 truncate block">
              {release.androidPackage}
            </span>
          </Field>
        )}
        {showAndroidInfo && (
          <Field label="SDK">
            <span className="text-sm text-white/90 tabular-nums">
              {release.minSdkVersion ?? '—'}
              <span className="text-neutral-500 mx-1.5">→</span>
              {release.targetSdkVersion ?? '—'}
            </span>
          </Field>
        )}
        <Field label="Version">
          <span className="text-sm text-white/90 tabular-nums">
            {release.versionName || '—'}
            {release.versionCode != null && (
              <span className="text-neutral-500 ml-1.5">
                ({release.versionCode})
              </span>
            )}
          </span>
        </Field>
        {showAndroidInfo && release.locales?.length > 0 && (
          <Field label="Languages" className="col-span-2 md:col-span-3">
            <div className="flex flex-wrap gap-1.5">
              {release.locales.map((locale, index) => (
                <span
                  key={`${locale}-${index}`}
                  className="px-2 py-0.5 rounded bg-white/[0.04] border border-white/5 text-xs text-neutral-300 font-mono"
                >
                  {locale}
                </span>
              ))}
            </div>
          </Field>
        )}
      </div>

      {hasFingerprints && (
        <Disclosure label="Fingerprints" count={fingerprintCount}>
          <div className="flex flex-col gap-4">
            {release.releaseFileHash && (
              <Field label="APK SHA-256">
                <CopyableValue
                  value={release.releaseFileHash}
                  truncate={false}
                  className="text-xs"
                />
              </Field>
            )}
            {release.certificateFingerprint && (
              <Field label="Certificate SHA-256">
                <CopyableValue
                  value={release.certificateFingerprint}
                  truncate={false}
                  className="text-xs"
                />
              </Field>
            )}
          </div>
        </Disclosure>
      )}
    </Section>
  );
}
